import { usePlayerAccess } from './PlayerAccess'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { apiClient, createMutationId, type PaymentProvider } from '../api'
import { content } from '../content-adapter'
import { useVip, type VipTier } from './VipContext'
import { VipCeremony } from './VipCeremony'
import { playGameSound } from '../animation/gameAudio'
import { APPLE_STOREKIT_PRODUCTS, finishNativeStoreKitTransaction, nativeStoreKitAvailable, requestNativeStoreKitPurchase, requestNativeStoreKitRestore, type NativeStoreKitEvent } from '../nativeStoreKit'

type PayStage = 'checkout' | 'redirecting' | 'checking' | 'pending' | 'success' | 'error'
const goldBenefits = [`史实导航——${content.nodes.length} 节点孙割走过的路，金边高光提示（本服务不构成人生建议）`, '会员专属香蕉折扣（暂无香蕉）', '优先割韭菜权（排队中，前方还有 300 万人）', '孙割亲笔签名（电子版，他自己签的）']
const diamondBenefits = ['铁废物语音播报——系统大声骂你（浏览器语音，货真价实）', '时光回溯——任何选错自动倒带，金手替你落子正解', '支线也管——无正解节点按增益最优自动选', '尊号「本服最强韭菜」+ 钻石流光全家桶']

export function VipPanel({ onClose }: { onClose: () => void }) {
  const vip = useVip()
  const { nickname } = usePlayerAccess()
  const [selectedTier, setSelectedTier] = useState<VipTier>(() => vip.initialTier || 'vip')
  const [tier, setTier] = useState<VipTier | null>(null)
  const [stage, setStage] = useState<PayStage>('checkout')
  const [message, setMessage] = useState('')
  const [returnOrderId, setReturnOrderId] = useState<string | null>(() => vip.returnOrderId)
  const [ceremony, setCeremony] = useState<VipTier | null>(null)
  const [storeKitAvailable] = useState(() => nativeStoreKitAvailable())
  const [storeKitBusy, setStoreKitBusy] = useState(false)
  const processing = useRef(false)
  const paymentAbort = useRef<AbortController | null>(null)
  const attempt = useRef<{ key: string; mutationId: string } | null>(null)

  useEffect(() => {
    vip.selectTier(null)
  }, [])

  useEffect(() => {
    const onStoreKitEvent = (event: Event) => {
      const payload = (event as CustomEvent<NativeStoreKitEvent>).detail
      if (!payload || payload.source !== 'sun-storekit') return
      if (payload.status === 'cancelled') {
        setStoreKitBusy(false)
        setStage('checkout')
        setMessage('已取消 Apple 内购。')
        return
      }
      if (payload.status === 'pending') {
        setStoreKitBusy(false)
        setStage('pending')
        setMessage('Apple 已收到购买请求，待审核完成后会自动发放权益。')
        return
      }
      if (payload.status === 'empty') {
        setStoreKitBusy(false)
        setStage('error')
        setMessage('当前 Apple 账号没有可恢复的购买。')
        return
      }
      if (payload.status === 'failed' || !payload.productId || !payload.transactionId) {
        setStoreKitBusy(false)
        setStage('error')
        setMessage(payload.message || 'Apple 内购失败，请稍后重试。')
        return
      }

      const returnedTier: VipTier = payload.productId === APPLE_STOREKIT_PRODUCTS.svip ? 'svip' : 'vip'
      setSelectedTier(returnedTier)
      setTier(returnedTier)
      setStage('checking')
      setMessage('正在由服务端向 Apple 校验交易…')
      void apiClient.verifyApplePurchase({
        mutationId: createMutationId(),
        productId: payload.productId as typeof APPLE_STOREKIT_PRODUCTS[keyof typeof APPLE_STOREKIT_PRODUCTS],
        transactionId: payload.transactionId
      }).then(async () => {
        if (payload.transactionId) finishNativeStoreKitTransaction(payload.transactionId)
        await vip.refreshEntitlements()
        setStoreKitBusy(false)
        setStage('success')
        if (vip.soundEnabled) playGameSound('coin')
      }).catch(error => {
        setStoreKitBusy(false)
        setStage('error')
        setMessage(error instanceof Error ? error.message : 'Apple 交易校验失败，请稍后重试。')
      })
    }
    window.addEventListener('sun-storekit-result', onStoreKitEvent)
    return () => window.removeEventListener('sun-storekit-result', onStoreKitEvent)
  }, [vip])

  const confirm = async (orderId: string, signal?: AbortSignal) => {
    if (processing.current) return
    const controller = signal ? null : new AbortController()
    const requestSignal = signal || controller!.signal
    if (controller) paymentAbort.current = controller
    processing.current = true
    setStage('checking')
    setMessage('')
    try {
      const stored = await vip.getPayment(orderId, requestSignal)
      if (requestSignal.aborted) return
      const returnedTier: VipTier = stored.productId === 'sun_svip' ? 'svip' : 'vip'
      setSelectedTier(returnedTier)
      setTier(returnedTier)
      const order = (stored.status === 'pending' || stored.status === 'paid') ? await vip.confirmPayment(orderId, requestSignal) : stored
      if (requestSignal.aborted) return
      if (order.status === 'paid') {
        setStage('success')
        setReturnOrderId(null)
        vip.clearReturnOrder()
        if (vip.soundEnabled) playGameSound('coin')
      } else if (order.status === 'pending') {
        setStage('pending')
        setMessage('支付平台尚未确认到账。若你刚完成付款，请稍后再次查询。')
      } else {
        setStage('error')
        setMessage(order.status === 'closed' ? '订单已关闭，请重新下单。' : '支付未完成，请重新下单。')
        setReturnOrderId(null)
        vip.clearReturnOrder()
      }
    } catch (error) {
      if (requestSignal.aborted) return
      setTier(current => current || selectedTier)
      setStage('error')
      setMessage(error instanceof Error ? error.message : '支付状态查询失败，请重试。')
    } finally {
      if (paymentAbort.current?.signal === requestSignal) paymentAbort.current = null
      processing.current = false
    }
  }

  useEffect(() => {
    if (returnOrderId) void confirm(returnOrderId)
  }, [returnOrderId])

  const open = (value: VipTier) => {
    setSelectedTier(value)
    setTier(value)
    setStage('checkout')
    setMessage('')
    setReturnOrderId(null)
  }
  const closePay = () => {
    paymentAbort.current?.abort()
    paymentAbort.current = null
    processing.current = false
    setTier(null)
    setMessage('')
    if (returnOrderId) {
      setReturnOrderId(null)
      vip.clearReturnOrder()
    }
  }
  const payWithStoreKit = (value: VipTier) => {
    setSelectedTier(value)
    setTier(value)
    setStage('redirecting')
    setMessage('正在打开 Apple 内购…')
    setStoreKitBusy(true)
    if (!requestNativeStoreKitPurchase(value)) {
      setStoreKitBusy(false)
      setStage('error')
      setMessage('当前环境不支持 Apple 内购，请重新打开应用。')
    }
  }
  const restoreWithStoreKit = () => {
    setStage('checking')
    setMessage('正在查询 Apple 购买记录…')
    setStoreKitBusy(true)
    if (!requestNativeStoreKitRestore()) {
      setStoreKitBusy(false)
      setStage('error')
      setMessage('当前环境不支持恢复购买。')
    }
  }
  const pay = async (provider: PaymentProvider) => {
    if (!tier || processing.current) return
    if (provider === 'wechat' && /MicroMessenger/i.test(navigator.userAgent)) {
      setStage('error')
      setMessage('微信 H5 支付不能在微信内置浏览器中拉起，请使用右上角菜单在系统浏览器打开。')
      return
    }
    processing.current = true
    const controller = new AbortController()
    paymentAbort.current = controller
    setStage('redirecting')
    setMessage('')
    const key = `${tier}:${provider}`
    if (attempt.current?.key !== key) attempt.current = { key, mutationId: createMutationId() }
    try {
      const order = await vip.beginPayment(tier, provider, attempt.current.mutationId, controller.signal)
      if (controller.signal.aborted) return
      if (order.status === 'paid') {
        setStage('success')
        if (vip.soundEnabled) playGameSound('coin')
        return
      }
      if (!order.checkoutUrl) throw new Error('支付平台没有返回收银台地址，请重试。')
      window.location.assign(order.checkoutUrl)
    } catch (error) {
      if (controller.signal.aborted) return
      setStage('error')
      setMessage(error instanceof Error ? error.message : '创建支付订单失败，请重试。')
    } finally {
      if (paymentAbort.current?.signal === controller.signal) paymentAbort.current = null
      processing.current = false
    }
  }
  const active = vip.vipStatus === 'active'
  const supreme = vip.svipStatus === 'active'
  const loading = vip.vipStatus === 'loading' || vip.svipStatus === 'loading'
  const statusText = (isActive: boolean) => isActive ? '✓ 已开通' : loading ? '读取中…' : '未开通'
  const startCeremony = (value: VipTier) => { paymentAbort.current?.abort(); paymentAbort.current = null; attempt.current = null; setTier(null); setCeremony(value) }
  const amount = tier === 'svip' ? '999.00' : '1.00'

  return <div className="vip-feature-panel">
    <div aria-hidden={Boolean(tier || ceremony)} inert={Boolean(tier || ceremony)}>
      {[selectedTier === 'svip'].map(diamond => <article className={`vip-card${diamond ? ' diamond' : ''}`} key={String(diamond)}>
        <div className="vip-head"><span className="vip-crown">{diamond ? '💎' : '👑'}</span><div><div className="vip-title">{diamond ? '宇宙至尊VIP · 自动改命' : '黑金VIP · 史实导航'}</div><div className="vip-sub">{diamond ? '选错就骂你，骂完替你选' : '一个讲“花钱买捷径”的人的模拟器'}</div></div><span className={`vip-state ${(diamond ? supreme : active) ? 'on' : 'off'}`}>{statusText(diamond ? supreme : active)}</span></div>
        {(diamond ? diamondBenefits : goldBenefits).map((benefit, index) => <div className="vip-benefit" key={benefit}><span className="vb-num">{'①②③④'[index]}</span>{benefit}</div>)}
        <div className="vip-note">{diamond ? '💎 含 1 元版全部权益。999 元买一个自动玩游戏的挂，这就是人生赢家的样子。' : '💡 三条暗设计，不告诉你：高光只照孙割走过的路，不保证是活路；属性偏离判定不受影响；支线内钞能力失灵。'}</div>
      </article>)}
      {vip.error && <p className="inline-error" role="alert">{vip.error}</p>}
      {selectedTier === 'svip'
        ? supreme ? <><button className="btn-vip-open gray" onClick={onClose}>宇宙至尊VIP 已生效 · 全场自动改命中</button><button className="btn-vip-open diamond" onClick={() => startCeremony('svip')}>重温宇宙至尊降临</button></> : <button className="btn-vip-open diamond" disabled={loading} onClick={() => open('svip')}>开通宇宙至尊VIP · ¥999.00</button>
        : active ? <><button className="btn-vip-open gray" onClick={onClose}>VIP 已生效 · 史实导航已开启</button><button className="btn-vip-open" onClick={() => startCeremony('vip')}>重温黑金卡降临</button></> : <button className="btn-vip-open" disabled={loading} onClick={() => open('vip')}>开通黑金VIP · ¥1.00</button>}
      <p className="vip-account-note">{nickname && `${nickname}，你的 VIP 通行证`}<br />真实付款将在支付平台确认到账后绑定当前账号，换设备登录仍可使用。</p>
    </div>
    {tier && <div id="pay-overlay" className="vip-pay-overlay" onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); closePay() } }}><div className="pay-box" role="region" aria-label="会员收银台" aria-live="polite">
      {(stage === 'checkout' || stage === 'error') && <><div className="pay-title">确认付款</div><div className="pay-shop">孙割集团 · {tier === 'svip' ? '宇宙至尊' : '韭菜'}事业部</div><div className="pay-item"><span className="pi-icon">{tier === 'svip' ? '💎' : '🎫'}</span><div><div className="pi-name">{tier === 'svip' ? '宇宙至尊VIP · 自动改命套餐' : '黑金VIP · 史实导航'}</div><div className="pi-desc">{tier === 'svip' ? '选错自动回溯 + 铁废物语音播报' : `${content.nodes.length} 节点历史正解高光`} · 当前账号永久有效</div></div><span className="pi-price">¥{amount}</span></div><div className="pay-amount">¥ <small>{amount}</small></div><p className="vip-account-note">{storeKitAvailable ? '付款由 App Store 官方内购处理' : '付款将在微信或支付宝的官方收银台完成'}</p>{stage === 'error' && <p className="inline-error" role="alert">{message || vip.error || '支付未完成，请重试。'}</p>}{storeKitAvailable ? <><button autoFocus className="btn-pay" disabled={loading || storeKitBusy} onClick={() => payWithStoreKit(tier)}>使用 Apple 内购</button><button className="btn-pay alipay" disabled={loading || storeKitBusy} onClick={restoreWithStoreKit}>恢复购买</button></> : <><button autoFocus className="btn-pay" disabled={loading} onClick={() => void pay('wechat')}>微信支付</button><button className="btn-pay alipay" disabled={loading} onClick={() => void pay('alipay')}>支付宝支付</button></>}<button className="pay-cancel" disabled={storeKitBusy} onClick={closePay}>取消</button></>}
      {(stage === 'redirecting' || stage === 'checking') && <><div className="pay-stage"><div className="pay-spinner" /><div className="ps-txt">{stage === 'redirecting' ? '正在打开官方收银台…' : '正在向支付平台查询订单…'}</div><div className="ps-sub">不要关闭页面或重复付款</div></div><button className="pay-cancel" onClick={closePay}>取消并关闭</button></>}
      {stage === 'pending' && <><div className="pay-stage"><div className="pay-check pending">…</div><div className="ps-txt">等待支付平台确认</div></div><div className="pay-roast">{message}</div><button autoFocus className="btn-pay" onClick={() => returnOrderId && void confirm(returnOrderId)}>我已完成付款，再次查询</button><button className="pay-cancel" onClick={closePay}>稍后再说</button></>}
      {stage === 'success' && <><div className="pay-stage"><div className="pay-check">✓</div><div className="ps-txt pay-success">支付成功</div></div><div className="pay-roast">支付平台已确认到账，会员权益已绑定当前账号。</div><button autoFocus className={`btn-pay${tier === 'svip' ? ' diamond' : ''}`} onClick={() => startCeremony(tier)}>{tier === 'svip' ? '领取宇宙至尊镰刀 →' : '领取黑金卡 →'}</button></>}
    </div></div>}
    {ceremony && createPortal(<VipCeremony tier={ceremony} onComplete={() => setCeremony(null)} />, document.body)}
  </div>
}
